import type { ReactNode } from 'react';

/**
 * Мелкие элементы форм кабинета: поле, ввод, тумблер, строка списка.
 *
 * Вынесены из экранов, потому что услуги, специалисты, часы и отпуска — это четыре раза
 * одна и та же форма в шторке. Четыре копии инпута разъехались бы отступами и радиусами
 * на первой же правке, и владелец увидел бы четыре разных приложения в одном.
 *
 * Классы взяты из прототипа `pronto_booking.html` (`.fld`, `.inp`, `.tgl`, `.setrow`).
 * Цвета — только переменными темы: литерал вроде `bg-white` пережил бы переключение
 * Telegram в тёмную тему белым прямоугольником (см. `tailwind.config.js`).
 */

export function Field(props: { label: string; children: ReactNode }): JSX.Element {
  return (
    <label className="mb-3 block">
      <div className="mb-1.5 text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3">
        {props.label}
      </div>
      {props.children}
    </label>
  );
}

export function TextInput(props: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  /** `numeric` поднимает цифровую клавиатуру телефона: длительность и цена — числа. */
  numeric?: boolean;
  type?: 'text' | 'date';
}): JSX.Element {
  return (
    <input
      type={props.type ?? 'text'}
      value={props.value}
      onChange={(event) => props.onChange(event.target.value)}
      placeholder={props.placeholder ?? ''}
      inputMode={props.numeric ? 'numeric' : 'text'}
      className="w-full rounded-[12px] bg-surface-2 px-3.5 py-3 text-[14.5px] font-semibold text-ink outline-none placeholder:font-medium placeholder:text-ink-3 focus:shadow-[inset_0_0_0_1.5px_var(--accent)]"
    />
  );
}

/** Две колонки — «Длительность / Цена», «Начало / Время». */
export function FieldPair(props: { children: ReactNode }): JSX.Element {
  return <div className="grid grid-cols-2 gap-2.5">{props.children}</div>;
}

export function Toggle(props: {
  on: boolean;
  onChange: (on: boolean) => void;
  label: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={props.on}
      disabled={props.disabled ?? false}
      onClick={() => props.onChange(!props.on)}
      className="mb-3 flex w-full items-center justify-between gap-3 rounded-[12px] bg-surface-2 px-3.5 py-3 text-left disabled:opacity-50"
    >
      <span className="text-[14px] font-semibold text-ink">{props.label}</span>
      <span
        className={`relative h-[26px] w-[44px] shrink-0 rounded-full transition ${
          props.on ? 'bg-accent' : 'bg-hair'
        }`}
      >
        <span
          className={`absolute top-[3px] h-5 w-5 rounded-full bg-on-accent transition-all ${
            props.on ? 'left-[21px]' : 'left-[3px]'
          }`}
        />
      </span>
    </button>
  );
}

/** Карточка списка. `onClick` делает её кнопкой — иначе это просто строка. */
export function Row(props: {
  children: ReactNode;
  onClick?: (() => void) | undefined;
  dimmed?: boolean;
}): JSX.Element {
  const look = `mb-2 w-full rounded-card bg-surface px-3.5 py-3.5 text-left shadow-[inset_0_0_0_1px_var(--hair)] ${
    // Выключенная позиция гасится, но НЕ прячется: владелец должен видеть то, что выключил,
    // иначе «услуга пропала» читается как потеря данных.
    props.dimmed ? 'opacity-55' : ''
  }`;

  if (!props.onClick) return <div className={look}>{props.children}</div>;
  return (
    <button type="button" onClick={props.onClick} className={`${look} transition`}>
      {props.children}
    </button>
  );
}

/**
 * Ошибка ввода в шторке.
 *
 * Отдельно от `ErrorState`: тот показывает отказ сервера по ключу i18n, а здесь текст уже
 * переведён экраном. Общий компонент потребовал бы заворачивать локальную проверку
 * в фальшивый `ApiFailure` — то есть выдавать свою ошибку за серверную.
 */
export function FormError(props: { text: string }): JSX.Element {
  return (
    <div className="mb-2.5 rounded-[12px] bg-[color-mix(in_srgb,var(--red)_12%,var(--surface))] px-3.5 py-3 text-[12.5px] leading-[1.45] text-danger shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--red)_35%,transparent)]">
      {props.text}
    </div>
  );
}

/** Подпись под списком или полем: пояснение, а не ошибка. */
export function Note(props: { children: ReactNode }): JSX.Element {
  return (
    <div className="px-1 pt-1 text-[12px] leading-[1.5] text-ink-3">{props.children}</div>
  );
}
